/**
 * Revenue Recognized vs Billed Chart Component
 * Compares monthly revenue recognition to billed amounts
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid } from 'recharts'
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend } from '@/components/ui/chart'
import type { ChartConfig } from '@/components/ui/chart'
import type { MonthlyData } from '@/types/ui/dashboard'
import { useFormatters } from '@/hooks/use-formatters'
import { ChartExportMenu } from './ChartExportMenu'
import { TimePeriodToggle } from './TimePeriodToggle'
import { exportToCSV, exportToExcel, exportToPNG, generateFilename } from '@/lib/utils/export-utils'
import { aggregateDataByTimePeriod, formatPeriodForExport, type TimePeriod } from '@/lib/utils/dashboard-utils'
import { useState, useMemo } from 'react'

interface RevenueRecognizedChartProps {
  data: MonthlyData[]
  billedToDate: number
  revenueToDate: number
  engagementId?: string | number
}

export function RevenueRecognizedChart({ data, billedToDate, revenueToDate, engagementId = 0 }: RevenueRecognizedChartProps) {
  const { formatCurrencyShort, formatMoney } = useFormatters()
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('monthly')

  // Aggregate data based on selected time period
  const aggregatedData = useMemo(() => {
    return aggregateDataByTimePeriod(data, timePeriod)
  }, [data, timePeriod])

  // Transform data to show period revenue vs billed
  const chartData = aggregatedData.map((item) => {
    return {
      period: item.period,
      // Carried for exports only (MM-DD-YYYY Period column); the axis uses `period`.
      periodDate: item.periodDate,
      revenueRecognized: item.revenue,
      billed: item.billed,
    }
  })

  const chartConfig = {
    revenueRecognized: {
      label: "Revenue Recognized",
    },
    billed: {
      label: "Billed",
    },
  } satisfies ChartConfig

  const handleExportCSV = () => {
    const exportData = chartData.map(item => ({
      Period: formatPeriodForExport(item.periodDate),
      'Revenue Recognized': item.revenueRecognized,
      'Billed': item.billed,
    }))
    exportToCSV(exportData, generateFilename('RevenueRecognized', engagementId, 'csv'))
  }

  const handleExportExcel = () => {
    const exportData = chartData.map(item => ({
      Period: formatPeriodForExport(item.periodDate),
      'Revenue Recognized': item.revenueRecognized,
      'Billed': item.billed,
    }))
    exportToExcel(exportData, generateFilename('RevenueRecognized', engagementId, 'xlsx'), 'Revenue Recognized')
  }

  const handleExportPNG = () => {
    exportToPNG('revenue-recognized-chart', generateFilename('RevenueRecognized', engagementId, 'png'))
  }

  const hasData = aggregatedData && aggregatedData.length > 0

  return (
    <Card id="revenue-recognized-chart">
      <CardHeader className="py-3 px-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">Revenue Recognized vs Billed</CardTitle>
            <CardDescription> Revenue recognition and billing comparison</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <TimePeriodToggle value={timePeriod} onChange={setTimePeriod} />
            <ChartExportMenu 
              onExportCSV={handleExportCSV}
              onExportExcel={handleExportExcel}
              onExportPNG={handleExportPNG}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex items-center justify-center h-[300px] w-full">
            <p className="text-sm text-muted-foreground">No data available</p>
          </div>
        ) : (
          <>
        <ChartContainer config={chartConfig} className="h-[300px] w-full min-w-0 min-h-0">
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="period" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrencyShort}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  valueFormatter={(value) => formatMoney(Number(value))}
                />
              }
            />
            <ChartLegend />
            <Bar 
              dataKey="revenueRecognized" 
              fill="#3b82f6"
              name="Revenue Recognized"
            />
            <Line 
              type="monotone" 
              dataKey="billed" 
              stroke="#22c55e" 
              strokeWidth={2}
              name="Billed"
            />
          </ComposedChart>
        </ChartContainer>
        
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Revenue Recognized</p>
            <p className="text-sm font-semibold">{formatMoney(revenueToDate)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Billed</p>
            <p className="text-sm font-semibold">{formatMoney(billedToDate)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Unbilled</p>
            <p className="text-sm font-semibold text-amber-600">
              {formatMoney(revenueToDate - billedToDate)}
            </p>
          </div>
        </div>
        </>
        )}
      </CardContent>
    </Card>
  )
}

